<template>
  <transition
    name="slide-y-up"
    :duration="animationDuration"
    @after-enter="scrollModalToBottom"
  >
    <div
      v-show="show"
      ref="modal"
      class="modal fade"
      :class="[
        { 'show d-block': show },
        { 'd-none': !show },
        { 'modal-mini': type === 'mini' }
      ]"
      tabindex="-1"
      role="dialog"
      :aria-hidden="!show"
      @click.self="closeModal"
    >
      <div
        class="modal-dialog"
        role="document"
        :class="[
          { 'modal-notice': type === 'notice' },
          { 'modal-dialog-centered': centered },
          modalClasses
        ]"
      >
        <div
          class="modal-content"
          :class="[
            gradient ? `bg-gradient-${gradient}` : '',
            modalContentClasses
          ]"
        >
          <div
            v-if="$slots.header"
            class="modal-header"
            :class="[headerClasses]"
          >
            <slot name="header"></slot>
            <slot name="close-button">
              <button
                v-if="showClose"
                type="button"
                class="close"
                aria-label="Close"
                @click="closeModal"
              >
                <i class="material-icons card-icon">close</i>
              </button>
            </slot>
          </div>

          <div v-if="$slots.default" class="modal-body" :class="bodyClasses">
            <slot></slot>
          </div>

          <div v-if="$slots.footer" class="modal-footer" :class="footerClasses">
            <slot name="footer"></slot>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { defineComponent, ref, watch, onMounted, onUnmounted } from 'vue';

export default defineComponent({
  name: 'Modal',
  props: {
    show: Boolean,
    showClose: {
      type: Boolean,
      default: true
    },
    centered: {
      type: Boolean,
      default: false
    },
    appendToBody: {
      type: Boolean,
      default: true
    },
    scrollToBottom: {
      type: Boolean,
      default: true
    },
    type: {
      type: String,
      default: '',
      validator(value) {
        return ['', 'notice', 'mini'].includes(value);
      }
    },
    modalClasses: {
      type: [Object, String],
      default: ''
    },
    modalContentClasses: {
      type: [Object, String],
      default: ''
    },
    gradient: {
      type: String,
      default: ''
    },
    headerClasses: {
      type: [Object, String],
      default: ''
    },
    bodyClasses: {
      type: [Object, String],
      default: ''
    },
    footerClasses: {
      type: [Object, String],
      default: ''
    },
    animationDuration: {
      type: Number,
      default: 500
    }
  },
  emits: ['update:show', 'close'],
  setup(props, { emit }) {
    const modal = ref(null);

    const closeModal = () => {
      emit('update:show', false);
      emit('close');
    };

    const scrollModalToBottom = () => {
      if (!props.scrollToBottom || !modal.value) return;
      modal.value.scrollTop = modal.value.scrollHeight - modal.value.clientHeight;
    };

    watch(() => props.show, (val) => {
      const documentClasses = document.body.classList;
      if (val) {
        documentClasses.add('modal-open');
      } else {
        documentClasses.remove('modal-open');
      }
    });

    onMounted(() => {
      if (props.appendToBody && modal.value?.parentNode !== document.body) {
        document.body.appendChild(modal.value);
      }
    });

    onUnmounted(() => {
      if (props.appendToBody && modal.value?.parentNode === document.body) {
        document.body.removeChild(modal.value);
      }
    });

    return {
      modal,
      closeModal,
      scrollModalToBottom
    };
  }
});
</script>

<style scoped>
.modal.show {
  background-color: rgba(0, 0, 0, 0.3);
}

/* SlideYUp transition */
.slide-y-up-enter-active,
.slide-y-up-leave-active {
  transition: all 0.5s ease;
}
.slide-y-up-enter-from,
.slide-y-up-leave-to {
  transform: translateY(100px);
  opacity: 0;
}
.slide-y-up-enter-to,
.slide-y-up-leave-from {
  transform: translateY(0);
  opacity: 1;
}
</style>
